import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useDispatch } from "react-redux";
import { showAuth } from "../../redux/authSlice";
import { toast } from "react-toastify";
import proxy from "../../api/axios";
import { patchProblemNoteInCache } from "../../api/queries";

const ACCENT = "#137274";
const SAVE_DEBOUNCE_MS = 1500;
const MAX_NOTE_LENGTH = 20000;

const getCkEditorSx = (isDark) => ({
  "& .ck-editor__editable": {
    minHeight: 260,
    borderRadius: "0 0 8px 8px",
    fontSize: "0.9rem",
  },
  "& .ck.ck-editor__top .ck-sticky-panel .ck-toolbar": {
    borderRadius: "8px 8px 0 0",
  },
  ...(isDark && {
    "--ck-color-base-foreground": "#1c2128",
    "--ck-color-base-background": "#0d1117",
    "--ck-color-base-border": "#30363d",
    "--ck-color-base-action": "#137274",
    "--ck-color-base-focus": "#1a8f91",
    "--ck-color-base-text": "#e6edf3",
    "--ck-color-base-active": "#1a8f91",
    "--ck-color-base-active-focus": "#1a8f91",
    "--ck-color-base-error": "#e5534b",
    "--ck-color-button-default-background": "transparent",
    "--ck-color-button-default-hover-background": "#21262d",
    "--ck-color-button-default-active-background": "#21262d",
    "--ck-color-button-on-background": "#21262d",
    "--ck-color-button-on-hover-background": "#262c34",
    "--ck-color-button-on-active-background": "#2a3038",
    "--ck-color-button-action-background": ACCENT,
    "--ck-color-button-action-hover-background": "#0f5c5e",
    "--ck-color-button-action-text": "#ffffff",
    "--ck-color-dropdown-panel-background": "#161b22",
    "--ck-color-dropdown-panel-border": "#30363d",
    "--ck-color-input-background": "#0d1117",
    "--ck-color-input-border": "#30363d",
    "--ck-color-input-text": "#e6edf3",
    "--ck-color-list-background": "#161b22",
    "--ck-color-list-button-hover-background": "#21262d",
    "--ck-color-list-button-on-background": ACCENT,
    "--ck-color-list-button-on-text": "#ffffff",
    "--ck-color-panel-background": "#161b22",
    "--ck-color-panel-border": "#30363d",
    "--ck-color-toolbar-background": "#161b22",
    "--ck-color-toolbar-border": "#30363d",
    "--ck-color-tooltip-background": "#0d1117",
    "--ck-color-tooltip-text": "#e6edf3",
    "--ck-color-engine-placeholder-text": "#8b949e",
  }),
});

// Local unsaved-draft backup, per problem slug — survives a reload or a
// failed save (flaky connection, server hiccup) so in-progress notes aren't
// silently lost while the debounce/blur save hasn't gone through yet.
const draftKey = (slug) => `ch_note_draft_${slug}`;
const readDraft = (slug) => {
  try {
    return localStorage.getItem(draftKey(slug));
  } catch {
    return null;
  }
};
const writeDraft = (slug, value) => {
  try {
    localStorage.setItem(draftKey(slug), value);
  } catch {
    /* ignore quota / private mode */
  }
};
const clearDraft = (slug) => {
  try {
    localStorage.removeItem(draftKey(slug));
  } catch {
    /* ignore */
  }
};

const formatSavedAt = (date) => {
  if (!date) return null;
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin === 1) return "1 min ago";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr === 1) return "1 hour ago";
  if (diffHr < 24) return `${diffHr} hours ago`;
  return new Date(date).toLocaleDateString();
};

const ProblemNotesTab = ({
  slug,
  isLoggedIn,
  initialNote = "",
  initialUpdatedAt = null,
  onNoteChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const border = isDark ? "#30363d" : "rgba(15, 60, 70, 0.12)";
  const muted = isDark ? "#8b949e" : "#5f6b73";

  const [note, setNote] = useState(() => {
    const draft = readDraft(slug);
    return draft !== null ? draft : initialNote || "";
  });
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(initialUpdatedAt);
  const saveTimer = useRef(null);
  const lastSyncedSlugRef = useRef(slug);

  // Only re-seed local state when we've actually switched to a different
  // problem. `initialNote`/`initialUpdatedAt` also change every time OUR OWN
  // save echoes back through the parent (onNoteChange -> problemDetails ->
  // this prop) — resetting on every such change means: type, pause long
  // enough to trigger the debounced save, keep typing before that save's
  // response comes back, and the echo silently stomps your newer text back
  // down to what was saved a moment earlier. Scoping the reset to slug
  // changes only avoids fighting with in-progress typing.
  useEffect(() => {
    if (lastSyncedSlugRef.current === slug) return;
    lastSyncedSlugRef.current = slug;
    // An unsaved local draft (from a reload or a save that never went
    // through) is more likely to be what the user actually wants than
    // whatever the server last confirmed.
    const draft = readDraft(slug);
    setNote(draft !== null ? draft : initialNote || "");
    setSavedAt(initialUpdatedAt);
    setStatus("idle");
  }, [slug, initialNote, initialUpdatedAt]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const saveNote = async (value) => {
    if (value.length > MAX_NOTE_LENGTH) return;
    setStatus("saving");
    try {
      const { data } = await proxy.put(`/problems/${slug}/save`, { note: value });
      setSavedAt(data.updatedAt);
      setStatus("saved");
      onNoteChange?.(data);
      patchProblemNoteInCache(slug, data.note);
      // Now safely persisted server-side — the draft would only cause
      // confusion if it lingered (e.g. shadowing a newer save from another tab).
      clearDraft(slug);
    } catch (err) {
      setStatus("error");
      toast.error(err.response?.data?.message || "Could not save note");
      // Keep the draft — the save didn't actually take effect.
    }
  };

  const handleChange = (value) => {
    setNote(value);
    // Write-through immediately (not debounced) — this is what actually
    // survives a reload/crash before the debounced save ever fires.
    writeDraft(slug, value);
    clearTimeout(saveTimer.current);
    if (value.length > MAX_NOTE_LENGTH) return;
    saveTimer.current = setTimeout(() => saveNote(value), SAVE_DEBOUNCE_MS);
  };

  const handleBlur = () => {
    clearTimeout(saveTimer.current);
    saveNote(note);
  };

  if (!isLoggedIn) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            px: 2,
            borderRadius: 2.5,
            border: `1px dashed ${border}`,
            bgcolor: isDark ? "#0d1117" : "#f8fafb",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 40, color: muted, mb: 1.5, opacity: 0.65 }} />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Sign in to save notes
          </Typography>
          <Typography variant="body2" sx={{ color: muted, maxWidth: 320, mx: "auto", mb: 2 }}>
            Keep your approach, complexity, and edge cases next to the problem —
            synced to your account.
          </Typography>
          <Button
            variant="contained"
            onClick={() => dispatch(showAuth())}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: ACCENT,
              boxShadow: "none",
              "&:hover": { bgcolor: "#0f5c5e", boxShadow: "none" },
            }}
          >
            Sign in
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.35 }}>
        <EditNoteOutlinedIcon sx={{ color: ACCENT, fontSize: 22 }} />
        <Typography sx={{ fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.02em" }}>
          My Notes
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: muted, fontSize: "0.82rem", mb: 1.5 }}>
        Private to you — approach, time/space complexity, edge cases, revision reminders.
      </Typography>

      <Box
        onBlur={handleBlur}
        sx={{
          border: `1px solid ${border}`,
          borderRadius: 2,
          overflow: "hidden",
          ...getCkEditorSx(isDark),
        }}
      >
        <CKEditor
          key={slug}
          editor={ClassicEditor}
          data={note}
          config={{
            placeholder: "Your approach, time/space complexity, edge cases…",
          }}
          onChange={(_, editor) => handleChange(editor.getData())}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
        <Typography
          variant="caption"
          sx={{ color: note.length > MAX_NOTE_LENGTH ? "#e5534b" : muted }}
        >
          {note.length > MAX_NOTE_LENGTH &&
            `Note is too long by ${note.length - MAX_NOTE_LENGTH} characters — trim it to keep saving.`}
        </Typography>
        <Typography variant="caption" sx={{ color: status === "error" ? "#e5534b" : muted }}>
          {status === "saving" && "Saving…"}
          {status === "saved" && `Saved${savedAt ? ` · ${formatSavedAt(savedAt)}` : ""}`}
          {status === "error" && "Could not save — check your connection"}
          {status === "idle" && savedAt && `Saved · ${formatSavedAt(savedAt)}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProblemNotesTab;
